import React, { useState } from 'react'
import { Box, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useModalState } from 'packages/core'
import { mobileScreenWidth } from 'utils'
import ActionModal from './ActionModal'
import ApplicationsSelection from './ApplicationsSelection'
import { EidButton } from 'packages/eid-ui'
import {
    useAssignApplicationToCategory,
    useRemoveApplicationFromCategory,
} from 'hooks/ssoAppsHooks'
import { ALL_SSO_APPLICATIONS_KEY } from '../../hooks/ssoAppsHooks'
import { useQueryClient } from '@tanstack/react-query'

export const ItemContainer = styled(Box)({
    padding: '15px 0px',
    [`@media (max-width:${mobileScreenWidth})`]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
})

const ManageApplicationsButton = ({ label = '', category }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [modalOpen, openModal, closeModal] = useModalState()
    const formRef = React.useRef(null)
    const { mutateAsync: assignApplicationToCategory } =
        useAssignApplicationToCategory()
    const { mutateAsync: removeApplicationFromCategory } =
        useRemoveApplicationFromCategory()

    const [isSaving, setIsSaving] = useState(false)
    const handleSave = async () => {
        try {
            setIsSaving(true)
            if (formRef.current) {
                const formData = formRef.current.getFormData()
                const { Added, Removed } = formData

                const addedApplicationIds = Added.map((item) => item.id)
                const removedApplicationIds = Removed.map((item) => item.id)

                const apiCalls = []

                if (addedApplicationIds.length > 0) {
                    apiCalls.push(
                        assignApplicationToCategory({
                            categoryId: category?.id,
                            protectedApplicationsResourceIds:
                                addedApplicationIds,
                        }),
                    )
                }

                if (removedApplicationIds.length > 0) {
                    apiCalls.push(
                        removeApplicationFromCategory({
                            categoryId: category?.id,
                            protectedApplicationsResourceIds:
                                removedApplicationIds,
                        }),
                    )
                }

                if (apiCalls.length > 0) {
                    await Promise.all(apiCalls)
                }
                //Refetch the sso applications
                queryClient.invalidateQueries({
                    queryKey: [ALL_SSO_APPLICATIONS_KEY],
                })
                setIsSaving(false)
                closeModal()
            }
        } catch (error) {
            console.error('Error assigning category to application:', error)
        }
    }
    return (
        <>
            {modalOpen && (
                <ActionModal
                    headerTitle={label}
                    onSubmit={handleSave}
                    onClose={() => {
                        closeModal()
                    }}
                    style={{ width: '100%' }}
                    actionButtonLabel={t('Save')}
                    disabled={isSaving}
                    isSubmitting={isSaving}
                >
                    <ApplicationsSelection
                        ref={formRef}
                        assignedUrl={
                            '/api/ProtectedApplicationResource/SSOApplications'
                        }
                        assignedQueryFilters={{
                            categoryId: category?.id,
                            recentlyUsed: false,
                            showAssignedOnly: true,
                        }}
                        unAssignedUrl={
                            '/api/ProtectedApplicationResource/SSOApplications'
                        }
                        unAssignedQueryFilters={{
                            categoryId: category?.id,
                            recentlyUsed: false,
                            showAssignedOnly: false,
                        }}
                    />
                </ActionModal>
            )}

            <EidButton.DropdownOptionsButton
                loading={false}
                label={label}
                onClick={openModal}
                data-ai-explain="Manage which applications are in this category"
                parentStyle={{
                    height: '4rem',
                    fontSize: '1.5rem',
                    width: '16.9rem',
                }}
            />
        </>
    )
}

export default ManageApplicationsButton
